import dayjs from '~/timeInit';

import type { Campaign } from './data';
import type { Filters } from './model';

export const INITIAL_FILTERS: Filters = {
  current: 'Double Six Luxury Hotel LPC',
  comparison: 'Double Six Luxury Hotel LPC',
  from: '2026-01-27',
  to: '2026-03-23',
  cmpFrom: '2025-12-02',
  cmpTo: '2026-01-26',
  refund: 'Before Refund',
  currency: 'Vendor Currency',
  room: 'All',
  packageType: 'All',
  booking: 'All',
  section: 'Performance',
  region: 'Strategic Territory',
};

/** Presentation, highlighting and picker state never change the analytical population. */
export function reportFilters(params: URLSearchParams): Filters {
  return Object.fromEntries(
    Object.entries(INITIAL_FILTERS).map(([key, value]) => [key, params.get('lpc_' + key) ?? value]),
  ) as Filters;
}

export function reportView(params: URLSearchParams) {
  const legacyTab = Number(params.get('lpc_tab') || 1);
  const pcm = [3, 4].includes(legacyTab);
  const currentOnly = params.has('lpc_only') ? params.get('lpc_only') === '1' : legacyTab === 2;
  return { pcm, currentOnly, tab: pcm ? (legacyTab === 4 ? 4 : 3) : currentOnly ? 2 : 1 };
}

export function reportingRequest(filters: Filters) {
  const from = [filters.from, filters.cmpFrom].sort()[0];
  const to = [filters.to, filters.cmpTo].sort()[1];
  return {
    offers: [...new Set([filters.current, filters.comparison])].filter(Boolean),
    lens_period: { from, to },
    current_from: [filters.from],
    current_to: [filters.to],
  };
}

export function dateProblem(filters: Filters) {
  for (const [label, from, to] of [
    ['Current', filters.from, filters.to],
    ['Comparison', filters.cmpFrom, filters.cmpTo],
  ]) {
    if (
      !from ||
      !to ||
      !dayjs(from).isValid() ||
      !dayjs(to).isValid() ||
      dayjs(from).format('YYYY-MM-DD') !== from ||
      dayjs(to).format('YYYY-MM-DD') !== to ||
      from > to
    ) {
      return `${label} dates must be valid and in order.`;
    }
  }
  if (filters.current === filters.comparison && filters.from <= filters.cmpTo && filters.cmpFrom <= filters.to) {
    return 'The same offer needs separate, non-overlapping current and comparison periods.';
  }
  return undefined;
}

export function filterChanges(initial: Filters, draft: Filters): Record<string, string> {
  return Object.fromEntries(Object.entries(draft).filter(([key, value]) => initial[key] !== value));
}

export function dateRange(from: string, to: string) {
  return dayjs(from).isValid() && dayjs(to).isValid()
    ? `${dayjs(from).format('D MMM YYYY')} – ${dayjs(to).format('D MMM YYYY')}`
    : 'Choose dates';
}

export function catalogueDateProblem(from: string, to: string) {
  return !from || !to || !dayjs(from).isValid() || !dayjs(to).isValid() ||
    dayjs(from).format('YYYY-MM-DD') !== from || dayjs(to).format('YYYY-MM-DD') !== to || from > to
    ? 'Transaction dates must be valid and in order.'
    : undefined;
}

export type CatalogueFilters = { search: string; country: string[]; destination: string[]; currency: string[] };
export function filterCatalogue(campaigns: Campaign[], filters: CatalogueFilters) {
  const search = filters.search.trim().toLocaleLowerCase();
  return campaigns.filter(
    (campaign) =>
      (!search ||
        [campaign.offer_opportunity_name, campaign.offer_bk, campaign.vendor_bk]
          .join(' ')
          .toLocaleLowerCase()
          .includes(search)) &&
      (!filters.country.length || filters.country.includes(campaign.offer_country_name || 'Unknown')) &&
      (!filters.destination.length || filters.destination.includes(campaign.offer_geo_area_level_1 || 'Unknown')) &&
      (!filters.currency.length || campaign.vendor_currencies?.some((currency) => filters.currency.includes(currency))),
  );
}
